import { CellId, uuid } from "@hex/common";
import { editor as Editor, Uri } from "monaco-editor";
import { useEffect, useRef } from "react";

import { useOnFinalUnmount } from "../../../hooks/useOnFinalUnmount";

interface useImperativeModelArgs {
  language: string;
  cellId: CellId;
}

const modelsByCellId: Record<string, Editor.ITextModel | undefined> = {};

/**
 * Initializes an empty model. Does not accept a source argument as source is to be
 * entirely controlled by the consumer via Monaco edit operations.
 */
export function useImperativeModel({
  cellId,
  language,
}: useImperativeModelArgs): Editor.ITextModel {
  const modelRef = useRef<Editor.ITextModel>();

  if (!modelRef.current) {
    modelRef.current = Editor.createModel(
      "",
      language,
      Uri.from({
        scheme: "inmemory",
        authority: "model",
        path: `/${uuid()}`,
      }),
    );
  }

  useEffect(() => {
    modelsByCellId[cellId] = modelRef.current;
    return () => {
      delete modelsByCellId[cellId];
    };
  }, [cellId]);

  useOnFinalUnmount(() => {
    modelRef.current?.dispose();
  });

  return modelRef.current;
}

export function getImperativeModel(
  cellId: CellId,
): Editor.ITextModel | undefined {
  return modelsByCellId[cellId];
}
